import React, { useMemo, useState, useContext, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Card, FormLayout, Select, Banner, Stack } from "@shopify/polaris";
import { useTranslation } from "react-i18next";

import TabContainer from "../../components/components/TabContainer";
import SEOTemplateForm from "./SeoTemplate/Form";

import { useUpdateSEOTemplates } from '../../hooks/queries/seoTemplates';
import { getLanguageName, getLocaleOptions } from '../../utilities/localeNames';

import { AppContext } from "../../components/global/appContext";

export default function SeoTemplatesForms({ initialData }) {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  
  // Extract data from new structure
  const initialSEOTemplates = initialData?.seo_templates || [];
  const availableLocales = initialData?.available_locales || ['en'];
  const defaultLocale = initialData?.current_locale || 'en';
  const localizationEnabled = initialData?.localization_enabled || false;
  
  const [SEOTemplates, setSEOTemplates] = useState(initialSEOTemplates);
  const [selectedLocale, setSelectedLocale] = useState(defaultLocale);
  
  // Get all available locale codes and ensure default locale is first
  const getAllAvailableLocaleCodes = () => {
    // Convert to array of locale codes
    const localeCodes = availableLocales.map(locale => 
      typeof locale === 'object' ? locale.code : locale
    );
    
    // Remove default locale from its current position and put it first
    const filteredCodes = localeCodes.filter(code => code !== defaultLocale);
    return [defaultLocale, ...filteredCodes];
  };
  
  const allAvailableLocaleCodes = getAllAvailableLocaleCodes();
  
  const updateSEOTemplatesMutation = useUpdateSEOTemplates({
    onSuccess: (data) => {
      setSEOTemplates(data.seo_templates);
      appContext.setMessage(t("seoTemplates.messages.saveSuccess"));
    },
    onError: (error) => {
      setSEOTemplates(error.response?.data?.seo_templates || SEOTemplates);
      appContext.setMessage(t("seoTemplates.messages.saveError"), true);
    },
  });

  const getTemplateIndexByID = (id) =>
    SEOTemplates.findIndex((t) => t.id == id);

  const getInitialTemplateByID = (id) =>
    initialSEOTemplates.find((t) => t.id == id);

  function updateTemplateById(id, newTemplate) {
    let newSEOTemplates = Object.values({
      ...SEOTemplates,
      [getTemplateIndexByID(id)]: newTemplate,
    });
    setSEOTemplates(newSEOTemplates);
  }

  function handleTemplateUpdate(id, key, value) {
    const templateIndex = getTemplateIndexByID(id);
    const template = SEOTemplates[templateIndex];
    
    // Update the current locale's translation
    let newTemplate = { ...template };
    
    // Deep clone translations to avoid mutation
    newTemplate.translations = JSON.parse(JSON.stringify(template.translations || {}));
    
    // If it's the default locale, update the main field
    if (selectedLocale === defaultLocale) {
      newTemplate[key] = value;
    } else {
      // Update translation in the nested structure
      newTemplate.translations[key] = newTemplate.translations[key] || {};
      newTemplate.translations[key][selectedLocale] = value;
    }
    
    // More robust change detection
    const initialTemplate = getInitialTemplateByID(id);
    newTemplate.unsaved = !templatesEqual(initialTemplate, newTemplate);
    
    console.log(`Template ${id} field ${key} changed to:`, value);
    console.log(`Template ${id} is now unsaved:`, newTemplate.unsaved);
    
    updateTemplateById(id, newTemplate);
  }

  function templatesEqual(a, b) {
    if (!a || !b) return false;
    
    // Compare main fields
    const fieldsToCompare = ['page_title', 'page_description', 'meta_title', 'meta_description'];
    for (let field of fieldsToCompare) {
      if ((a[field] || '') !== (b[field] || '')) {
        return false;
      }
    }
    
    // Compare translations
    const aTranslations = a.translations || {};
    const bTranslations = b.translations || {};
    
    // Get all field keys from both translations
    const allFields = new Set([...Object.keys(aTranslations), ...Object.keys(bTranslations)]);
    
    for (let field of allFields) {
      const aFieldTranslations = aTranslations[field] || {};
      const bFieldTranslations = bTranslations[field] || {};
      
      // Get all locales for this field
      const allLocales = new Set([...Object.keys(aFieldTranslations), ...Object.keys(bFieldTranslations)]);
      
      for (let locale of allLocales) {
        if ((aFieldTranslations[locale] || '') !== (bFieldTranslations[locale] || '')) {
          return false;
        }
      }
    }
    
    return true;
  }

  const anyUnsavedChanges = useMemo(
    () => SEOTemplates.some((t) => t.unsaved),
    [SEOTemplates]
  );

      // Get locale options for the selector - show only enabled languages and default
    const getLocaleOptionsForSelector = () => {
      return getLocaleOptions(allAvailableLocaleCodes);
    };
    
    // Helper function to get locale display name
    const getLocaleDisplayName = (localeCode) => {
      return getLanguageName(localeCode);
    };

  // Get current value for a field in selected locale
  const getCurrentLocaleValue = (template, field) => {
    if (selectedLocale === defaultLocale) {
      return template[field] || '';
    }
    
    return template.translations?.[field]?.[selectedLocale] || '';
  };

  // Get placeholder text from default locale
  const getPlaceholderText = (template, field) => {
    if (selectedLocale === defaultLocale) {
      return '';
    }
    
    return template[field] || template.translations?.[field]?.[defaultLocale] || '';
  };

  const [leavingPage, setLeavingPage] = useState(false);
  useEffect(() => {
    return () => {
      console.log("LEAVING PAGE");
      setLeavingPage(true);
    };
  }, []);

  // Prepare templates for saving - include both main fields and translations
  const prepareSaveData = () => {
    return SEOTemplates.filter(t => t.unsaved).map(template => ({
      id: template.id,
      resource_type: template.resource_type,
      page_title: template.page_title,
      page_description: template.page_description,
      meta_title: template.meta_title,
      meta_description: template.meta_description,
      translations: template.translations || {}
    }));
  };

  // Handle form submission (triggered by Save Bar)
  const handleSubmit = (e) => {
    e.preventDefault();
    updateSEOTemplatesMutation.mutate(prepareSaveData());
  };

  // Handle form reset (triggered by Discard button in Save Bar)
  const handleReset = (e) => {
    e.preventDefault();
    setSEOTemplates(initialSEOTemplates);
  };

  return (
    <form 
      data-save-bar 
      onSubmit={handleSubmit}
      onReset={handleReset}
    >
    <TabContainer
      sections={[
        {
          title: t("seoTemplates.title"),
          items: SEOTemplates.map((template) => {
            // Map template titles to translation keys
            const templateNameMap = {
              'landing': t("themes.templates.items.landing"),
              'directory': t("themes.templates.items.directory"),
              'country': t("themes.templates.items.country"),
              'region': t("themes.templates.items.region"),
              'city': t("themes.templates.items.city"),
              'location': t("themes.templates.items.location")
            };
            
            const translatedLabel = templateNameMap[template.title.toLowerCase()] || template.title;
            
            return {
              label: translatedLabel,
              url: `/seo-templates/${template.title.toLowerCase()}`,
              badge: Object.keys(template.errors || {}).length
                ? t("seoTemplates.badges.errors")
                : template.unsaved && t("seoTemplates.badges.unsaved"),
            };
          }),
        },
      ]}
    >
      <Stack vertical spacing="loose">
        {!localizationEnabled && (
          <Banner
            title={t("seoTemplates.banner.multipleLanguagesDisabled")}
            status="info"
            action={{
              content: t("seoTemplates.banner.enableInSettings"),
              url: '/settings/languages'
            }}
          >
            <p>
              {t("seoTemplates.banner.multipleLanguagesInfo", { locale: getLocaleDisplayName(defaultLocale) })}
            </p>
          </Banner>
        )}

        <Card sectioned>
          <FormLayout>
            <Select
              label={t("seoTemplates.language.label")}
              options={getLocaleOptionsForSelector()}
              value={selectedLocale}
              onChange={setSelectedLocale}
              prefix={<img src="/assets/LanguageTranslateIcon.svg" alt="Language" style={{ width: '20px', height: '20px' }} />}
              helpText={
                selectedLocale === defaultLocale 
                  ? t("seoTemplates.language.defaultHelp")
                  : t("seoTemplates.language.editingHelp", { 
                      locale: getLocaleDisplayName(selectedLocale),
                      defaultLocale: getLocaleDisplayName(defaultLocale)
                    })
              }
            />
          </FormLayout>
        </Card>

        <Routes>
          <Route
            index
            element={<Navigate to={SEOTemplates[0]?.title?.toLowerCase() || ''} />}
          />
          {SEOTemplates.map((template) => (
            <Route
              key={template.id}
              path={template.title.toLowerCase()}
              element={
                <SEOTemplateForm
                  SEOTemplate={template}
                  selectedLocale={selectedLocale}
                  defaultLocale={defaultLocale}
                  getCurrentLocaleValue={getCurrentLocaleValue}
                  getPlaceholderText={getPlaceholderText}
                  getLocaleDisplayName={getLocaleDisplayName}
                  discardChanges={() =>
                    updateTemplateById(
                      template.id,
                      getInitialTemplateByID(template.id)
                    )
                  }
                  handleUpdate={(key, value) =>
                    handleTemplateUpdate(template.id, key, value)
                  }
                />
              }
            />
          ))}
        </Routes>
      </Stack>
    </TabContainer>
    </form>
  );
}
